import { useCallback } from 'react';

import type { ActorListEnum, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { actorForm2ApiMapper } from '../../../../Actor/mappers/actor.form2api.mapper';
import type { ActorForm_Actor } from '../../../../Actor/fragments/actor.fragment';
import type { ActorFormTab } from '../../../../Actor/enums/tab.enum';

import type { CreateMutation } from '../types/props.type';

interface Params {
  readonly folderBusinessEntityId: Id;
  readonly list: ActorListEnum;
  readonly directMutation?: CreateMutation;
  readonly handleCreateSuccess: () => void;
}

interface FormikArrayActions {
  readonly createInForm: (value: Readonly<ActorForm_Actor>) => void;
}

export const useCreateFolderBusinessEntityActorSubmit = ({
  folderBusinessEntityId,
  list,
  directMutation,
  handleCreateSuccess,
}: Readonly<Params>) => {
  const handleSubmitFactory = useCallback(
    ({ createInForm }: Readonly<FormikArrayActions>) =>
      ({
        values,
        memos,
        formikHelpers: { setSubmitting },
        setWizardError,
      }: Readonly<
        WizardSubmitHandlerParams<ActorForm_Actor, ActorFormTab>
      >) => {
        setWizardError(undefined);

        const onError = (error: unknown) => {
          setWizardError({ messages: userFriendlyErrors(error as BackError) });
          setSubmitting(false);
        };

        const onSuccess = () => {
          createInForm(values);

          handleCreateSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              actorableId: folderBusinessEntityId,
              list,
              actor: actorForm2ApiMapper(values),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [handleCreateSuccess, folderBusinessEntityId, directMutation, list],
  );

  return { handleSubmitFactory };
};
